import { AssessmentStatus as Assessment_Status_Enum } from '@risksmart-app/domain/src/types/consts/assessment-status';
import type { UpdateOversightAssessmentMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { UpdateOversightAssessmentDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

import type {
  UpdateOversightAssessmentInput,
  UpdateOversightAssessmentRequest,
} from './types';

export const useUpdateOversightAssessment = createMutationHook({
  name: 'updateOversightAssessment',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.oversight.update.mutationOptions({ onSuccess }),
  mapInput: ({
    OriginatingItemId: _legacyOnly,
    ...variables
  }: UpdateOversightAssessmentInput): UpdateOversightAssessmentRequest => ({
    ...variables,
    Title: variables.Title ?? '',
    Status: variables.Status ?? Assessment_Status_Enum.NotStarted,
  }),
  invalidate: (trpc) => [
    trpc.frontend.oversight.register.queryKey(),
    trpc.frontend.oversight.getById.queryKey(),
    // COI-471-style parity: the Apollo twin also evicts the base
    // assessment-result families (their ratings roll up into the
    // assessment) and re-fetches oversight-specific result reads that ARE
    // migrated. `GetOversightResultsByParentIdDocument` and
    // `GetAllOversightAssessmentResultsDocument` have NO tRPC twin yet
    // (RSP-6476 Cluster 2 thinking-point #1) — those two result-list reads
    // cannot be invalidated from this path until they migrate.
    trpc.frontend.oversight.oversightAssessmentCount.queryKey(),
    trpc.frontend.assessment.oversightAssessmentResultCount.queryKey(),
    trpc.frontend.assessment.oversightAssessmentRiskAssessmentResultsByRiskId.queryKey(),
    trpc.frontend.assessment.oversightAssessmentDocumentAssessmentResultsByDocumentId.queryKey(),
    trpc.frontend.assessment.oversightAssessmentObligationAssessmentResultsByObligationId.queryKey(),
    // COI-889: this hook rewrites the owner/contributor sets, whose triggers
    // maintain the grant cache `perm_v2_entity_permissions` reads (60-min
    // window, `refetchOnMount: false`, so it never self-heals).
    trpc.frontend.permission.getEntityPermissions.queryKey(),
  ],
  mapResponse: (result: {
    affected_rows: number;
  }): UpdateOversightAssessmentMutation => ({
    updateOversightAssessmentApi: {
      __typename: 'GenericMutationOutput',
      affected_rows: result.affected_rows,
    },
  }),
  graphqlDocument: UpdateOversightAssessmentDocument,
  // COI-622: strip the concurrency token — the legacy mutation input has no
  // OriginalTimestamp field, so only the tRPC leg locks on it.
  graphqlVariables: ({
    OriginalTimestamp: _token,
    ...variables
  }: UpdateOversightAssessmentInput) => ({
    object: variables,
  }),
  // The corresponding READS are NOT migrated and are cache-first, so a write
  // via tRPC still has to evict these or the Apollo-backed list/counter shows
  // pre-write data. Kept identical on both branches so they cannot drift.
  evictFields: [
    'risk_assessment_result',
    'document_assessment_result',
    'obligation_assessment_result',
    'oversight_assessment',
  ],
});
